import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import {
  Brain, Trash2, Download, Sparkles, ShieldCheck, X,
  MessageCircleHeart, Feather, BookOpen, ClipboardList, TrendingUp,
} from 'lucide-react';
import ContextualHelp from '@/components/ContextualHelp';

const MEMORY_HELP = {
  whatIs: "Everything Brotherhood+ has learned about you from your journal, coach chats, check-ins, lessons, and plans — visible in one place.",
  whyMatters: "The Coach and Ask Brotherhood use this memory to make recommendations feel personal instead of generic. You should always be able to see, delete, export, or turn it off.",
  howToUse: "Scan by category. Delete anything that feels off or outdated. Flip the toggle to pause memory anytime — future actions won't be remembered until you turn it back on.",
  tips: [
    "Memory is private to your account.",
    "Turning memory off does NOT delete existing memory — use 'Clear all' for that.",
    "Export gives you a plain-text JSON copy you can save anywhere.",
  ],
};

const KIND_ICON = {
  topic: BookOpen, goal: TrendingUp, person: MessageCircleHeart, value: Sparkles,
  struggle: Feather, pattern: ClipboardList, identity: Brain,
  preference: ShieldCheck, milestone: Sparkles,
};

const SOURCE_LABEL = {
  journal: 'Journal', coach: 'Coach', lesson: 'Lesson',
  checkin: 'Check-in', plan: 'Plan', system: 'System',
};

function KindGroup({ kind, label, items, onDelete }) {
  const Icon = KIND_ICON[kind] || Sparkles;
  if (!items?.length) return null;
  return (
    <section className="mb-8" data-testid={`memory-group-${kind}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-gold" strokeWidth={1.75} />
        <p className="text-[10px] uppercase tracking-[0.22em] text-navy/60 font-semibold">{label}</p>
        <span className="text-xs text-navy/40">· {items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`memory-item-${it.id}`}
            className="bg-white rounded-2xl p-4 flex items-start gap-3 card-soft group"
          >
            <div className="flex-1 min-w-0">
              <p className="text-navy leading-relaxed text-[15px]">{it.value}</p>
              <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-widest text-navy/40">
                {(it.source_types || []).map(s => (
                  <span key={s} className="px-2 py-0.5 rounded-full bg-beige text-navy/60 font-semibold">{SOURCE_LABEL[s] || s}</span>
                ))}
                {typeof it.confidence === 'number' && (
                  <span className="ml-auto">confidence {Math.round(it.confidence * 100)}%</span>
                )}
              </div>
            </div>
            <button
              onClick={() => onDelete(it.id)}
              data-testid={`memory-delete-${it.id}`}
              aria-label="Forget this"
              className="text-navy/30 hover:text-red-600 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-smooth"
            >
              <X size={16} strokeWidth={1.75} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Memory() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [kinds, setKinds] = useState({});
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [mem, settings] = await Promise.all([
        http.get('/memory'),
        http.get('/memory/settings'),
      ]);
      setItems(mem.data.items || []);
      setGrouped(mem.data.grouped || {});
      setKinds(mem.data.kinds || {});
      setEnabled(!!settings.data.enabled);
    } catch {
      toast.error("Couldn't load your memory");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await http.put('/memory/settings', { enabled: next });
      toast.success(next ? 'Memory turned on' : 'Memory paused');
    } catch { toast.error("Couldn't update setting"); setEnabled(!next); }
  };

  const removeOne = async (id) => {
    try {
      await http.delete(`/memory/${id}`);
      setItems(items.filter(i => i.id !== id));
      const g = {};
      for (const [k, arr] of Object.entries(grouped)) {
        const filtered = (arr || []).filter(i => i.id !== id);
        if (filtered.length) g[k] = filtered;
      }
      setGrouped(g);
      toast.success('Forgotten');
    } catch { toast.error("Couldn't delete"); }
  };

  const clearAll = async () => {
    if (!window.confirm('Erase everything Brotherhood+ has learned about you? This cannot be undone.')) return;
    try {
      const r = await http.delete('/memory');
      toast.success(`Cleared ${r.data.deleted || 0} memories`);
      setItems([]); setGrouped({});
    } catch { toast.error("Couldn't clear memory"); }
  };

  const exportMemory = async () => {
    try {
      const r = await http.get('/memory/export');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brotherhood-plus-memory-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Memory exported');
    } catch { toast.error("Couldn't export"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-14" data-testid="memory-page">
      <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Brotherhood+</p>
      <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight flex items-start gap-2 flex-wrap">
        What Brotherhood+ knows about you.
        <ContextualHelp pageTitle="Memory" content={MEMORY_HELP} testid="memory-help"/>
      </h1>
        <p className="text-navy/60 mt-3 text-base lg:text-lg leading-relaxed max-w-2xl">
          Everything on this page is remembered so your Coach and Ask Brotherhood feel personal &mdash; not generic.
          Delete anything you&apos;d rather forget, or turn memory off entirely.
        </p>

      {/* Controls */}
      <div className="mt-8 bg-white rounded-3xl p-5 lg:p-6 card-soft">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <p className="font-heading text-lg text-navy">Memory</p>
            <p className="text-xs text-navy/55 mt-0.5">{enabled ? 'On — Brotherhood+ is learning as you go.' : 'Off — no new memories will be captured.'}</p>
          </div>
          <button
            onClick={toggle}
            data-testid="memory-toggle"
            role="switch"
            aria-checked={enabled}
            className={`relative w-14 h-8 rounded-full transition-smooth ${enabled ? 'bg-navy' : 'bg-navy/15'}`}
          >
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-smooth ${enabled ? 'left-7' : 'left-1'}`} />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-5 flex-wrap">
          <button
            onClick={exportMemory}
            data-testid="memory-export"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-beige/70 hover:bg-beige text-navy transition-smooth"
          >
            <Download size={13} strokeWidth={1.75} /> Export as JSON
          </button>
          <button
            onClick={clearAll}
            data-testid="memory-clear-all"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-red-50 hover:bg-red-100 text-red-700 transition-smooth"
          >
            <Trash2 size={13} strokeWidth={1.75} /> Clear all
          </button>
        </div>
      </div>

      {/* Items grouped by kind */}
      <div className="mt-10">
        {loading && <p className="text-sm text-navy/50">Loading your memory…</p>}
        {!loading && items.length === 0 && (
          <div className="bg-beige/50 rounded-3xl p-8 text-center" data-testid="memory-empty">
            <Brain size={28} className="mx-auto text-navy/40" strokeWidth={1.5} />
            <p className="font-heading text-xl text-navy mt-3">Nothing remembered yet.</p>
            <p className="text-sm text-navy/60 mt-1 max-w-md mx-auto">
              As you journal, chat with the Coach, complete lessons, and check in — Brotherhood+ will note durable things (topics, goals, people, patterns) here.
            </p>
          </div>
        )}
        {!loading && items.length > 0 && Object.entries(kinds).map(([k, label]) => (
          <KindGroup key={k} kind={k} label={label} items={grouped[k] || []} onDelete={removeOne} />
        ))}
      </div>

      <p className="text-[11px] text-navy/40 text-center mt-12 max-w-md mx-auto leading-relaxed">
        Your memory is private to your account. We never sell it, and it never leaves Brotherhood+ unless you export it yourself.
      </p>
    </div>
  );
}
